<template>
  <component :is="layoutTag">
    <div class="bloco">
      <div class="row align-items-start">
        <div class="col-md-8">
          <div class="titulo d-flex align-items-center">
            <span>Gestão de Receituários</span>
            <span class="realtime-pill ml-2" :class="realtimeConnected ? 'is-online' : 'is-offline'">
              {{ realtimeConnected ? 'Realtime online' : 'Realtime offline' }}
            </span>
          </div>
          <div class="subTitulo">
            Controle centralizado dos envios para SIAGRO (PR), SIGEN+ (SC) e SIG@ (RS), com foco nas receitas
            ainda não enviadas.
          </div>
        </div>
        <div class="col-md-4 text-md-right mt-3 mt-md-0">
          <button class="btn btn-outline-info mr-2 mb-2" @click="showWebhookGuideModal = true">
            Orientações Webhook
          </button>
          <button class="btn btn-outline-secondary mr-2 mb-2" @click="showFilterSidebar = true">
            Filtros
          </button>
          <button class="btn btn-primary mb-2" @click="showIntegrationSidebar = true">
            Enviar Receituários
          </button>
        </div>
      </div>

      <div class="borda"></div>

      <div class="purchase-box mb-4">
        <div class="row align-items-start">
          <div class="col-12 col-lg-7">
            <div class="purchase-title">Pedidos de Compra</div>
            <div class="purchase-subtitle">
              Centralize os receituários que ainda precisam ser gerados e acompanhe o progresso operacional.
            </div>
          </div>
          <div class="col-12 col-lg-5 mt-2 mt-lg-0">
            <div class="pipeline purchase-flow-compact mb-0">
              <div class="pipeline-title">Fluxo operacional do período</div>
              <div class="pipeline-track">
                <div class="pipeline-segment segment-success" :style="{ width: sentPercent + '%' }"></div>
                <div class="pipeline-segment segment-warning" :style="{ width: pendingPercent + '%' }"></div>
                <div class="pipeline-segment segment-danger" :style="{ width: errorPercent + '%' }"></div>
              </div>
              <div class="pipeline-legends">
                <span><i class="dot dot-success"></i> Concluídas {{ sentPercent }}%</span>
                <span><i class="dot dot-warning"></i> Pendentes {{ pendingPercent }}%</span>
                <span><i class="dot dot-danger"></i> Alta {{ errorPercent }}%</span>
              </div>
            </div>
          </div>
        </div>

        <div class="row mt-2">
          <div class="col-12 col-md-4 mb-2">
            <div class="purchase-kpi">
              <div class="purchase-kpi-label">Pendentes para geração</div>
              <div class="purchase-kpi-value">{{ purchaseSummary.pending }}</div>
            </div>
          </div>
          <div class="col-12 col-md-4 mb-2">
            <div class="purchase-kpi">
              <div class="purchase-kpi-label">Receituários gerados</div>
              <div class="purchase-kpi-value">{{ purchaseSummary.generated }}</div>
            </div>
          </div>
          <div class="col-12 col-md-4 mb-2">
            <div class="purchase-kpi">
              <div class="purchase-kpi-label">Prioridade alta</div>
              <div class="purchase-kpi-value">{{ purchaseSummary.highPriority }}</div>
            </div>
          </div>
        </div>

        <div class="d-flex justify-content-end mb-2">
          <button class="btn btn-outline-danger btn-sm" @click="clearPurchaseOrdersToDemo">
            Limpar integrações
          </button>
        </div>

        <b-table striped hover responsive="sm" borderless bordered outlined small :items="purchaseOrders"
          :fields="purchaseFields" class="mt-2">
          <template v-slot:cell(prioridade)="row">
            <span :class="['purchase-badge', row.item.prioridade === 'Alta' ? 'purchase-high' : 'purchase-normal']">
              {{ row.item.prioridade }}
            </span>
          </template>

          <template v-slot:cell(statusPedido)="row">
            <span
              :class="['purchase-badge', row.item.statusPedido === 'Gerado' ? 'purchase-generated' : 'purchase-pending']">
              {{ row.item.statusPedido }}
            </span>
          </template>

          <template v-slot:cell(actions)="row">
            <button class="btn btn-sm"
              :class="row.item.statusPedido === 'Gerado' ? 'btn-outline-secondary' : 'btn-outline-primary'"
              @click="handlePedidoCompraAction(row.item)">
              {{ row.item.statusPedido === 'Gerado' ? 'Reabrir geração' : 'Gerar receituário' }}
            </button>
          </template>
        </b-table>
      </div>

      <div class="row">
        <div class="col-12 col-lg-6 mb-4">
          <div class="upload-baixa-box h-100">
            <div class="upload-baixa-title">Emissões por Responsável Técnico</div>
            <div class="upload-baixa-subtitle">
              Consolidação dos pedidos de compra por responsável técnico.
            </div>
            <b-table
              striped
              hover
              responsive="sm"
              borderless
              bordered
              outlined
              small
              :items="responsavelSummaryItems"
              :fields="responsavelSummaryFields"
            >
              <template v-slot:cell(pendentes)="row">
                <span class="summary-chip summary-pending">{{ row.item.pendentes }}</span>
              </template>
              <template v-slot:cell(geradas)="row">
                <span class="summary-chip summary-generated">{{ row.item.geradas }}</span>
              </template>
            </b-table>
          </div>
        </div>

        <div class="col-12 col-lg-6 mb-4">
          <div class="upload-baixa-box h-100">
            <div class="upload-baixa-title">Histórico de gerações</div>
            <div class="upload-baixa-subtitle">
              Use o histórico para dar baixa rápida das receitas enviadas com sucesso no portal (SIAGRO/SIG@).
            </div>

            <div v-if="!historyTimeline.length" class="text-muted small">
              Nenhum histórico registrado ainda.
            </div>

            <div v-else class="history-list">
              <div class="history-item" v-for="entry in historyTimeline" :key="entry.uid">
                <div class="history-main">
                  <div class="history-type">{{ entry.title }}</div>
                  <div class="history-periodo">{{ entry.subtitle }}</div>
                  <div class="history-meta">{{ entry.meta }}</div>
                </div>
                <button
                  v-if="entry.kind === 'GERACAO'"
                  class="btn btn-outline-success btn-sm"
                  @click="darBaixaPorHistorico(entry.raw)"
                >
                  Dar baixa
                </button>
                <button
                  v-else
                  class="btn btn-outline-secondary btn-sm"
                  @click="openSiagroErrorDetails(entry.raw)"
                >
                  Detalhes
                </button>
              </div>
            </div>

            <div class="upload-baixa-result mt-2" v-if="baixaResult">
              <div><strong>Baixa aplicada em:</strong> {{ baixaResult.orgao }}</div>
              <div><strong>Período:</strong> {{ baixaResult.periodo }}</div>
              <div><strong>Atualizadas:</strong> {{ baixaResult.updated }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <b-modal v-model="showMarkModal" title="Marcar receita como enviada" title-class="font-18">
      <div v-if="selectedItem">
        <p class="mb-2">
          Receita <strong>{{ selectedItem.numero }}</strong> - {{ selectedOrgaoLabel }}
        </p>
        <b-form-group label="Protocolo (opcional)">
          <b-form-input v-model="markForm.protocolo" placeholder="Ex: PR-2026-000912"></b-form-input>
        </b-form-group>
        <b-form-group label="Observação (opcional)">
          <b-form-textarea v-model="markForm.observacao" rows="3"
            placeholder="Informações do envio para rastreabilidade"></b-form-textarea>
        </b-form-group>
      </div>
      <template v-slot:modal-footer>
        <b-button variant="light" @click="showMarkModal = false">Cancelar</b-button>
        <b-button variant="success" @click="confirmMarkAsSent">Confirmar envio</b-button>
      </template>
    </b-modal>

    <b-modal v-model="showWebhookGuideModal" title="Orientações para Envio ao Webhook" size="lg" hide-footer>
      <div class="webhook-guide">
        <p class="mb-2">
          Use estas orientações para enviar os dados corretamente ao endpoint de webhook.
        </p>
        <ol class="pl-3 mb-3">
          <li>Configure o método HTTP como <strong>POST</strong>.</li>
          <li>Informe o header <code>Content-Type: application/json</code>.</li>
          <li>Envie o payload no formato JSON conforme exemplo abaixo.</li>
          <li>Valide se o <code>companyId</code> e <code>userId</code> estão corretos para roteamento realtime.</li>
          <li>Para simulação inicial, a dose e detalhes técnicos complementares serão resolvidos pelo mock do backend.</li>
        </ol>

        <div class="guide-label">URL sugerida</div>
        <div class="guide-box mb-3">{{ webhookGuideUrl }}</div>

        <div class="guide-label">Exemplo de payload</div>
        <pre class="guide-box">{{ webhookPayloadExample }}</pre>

        <div class="guide-label mt-3">Escopo inicial das simulações</div>
        <div class="guide-note mb-2">
          No primeiro momento, as simulações devem considerar este produto, culturas e alvos (tratamento de sementes).
        </div>
        <div class="guide-card mb-3">
          <div class="guide-card-title">Produto padrão: STANDAK TOP</div>
          <div class="row">
            <div class="col-12 col-lg-7 mb-2 mb-lg-0">
              <div class="guide-mini-title">Cultura: Soja</div>
              <ul class="guide-list">
                <li>Fungo-de-armazenamento (Aspergillus flavus)</li>
                <li>Mancha-púrpura-da-semente (Cercospora kikuchii)</li>
                <li>Antracnose (Colletotrichum dematium var. truncata)</li>
                <li>Podridão-de-Fusarium (Fusarium semitectum)</li>
                <li>Cancro haste (Phomopsis phaseoli f. sp. meridionalis)</li>
                <li>Phomopsis-da-semente (Phomopsis sojae)</li>
                <li>Vaquinha-verde-amarela (Diabrotica speciosa)</li>
                <li>Lagarta-elasmo (Elasmopalpus lignosellus)</li>
                <li>Coró (Lyogenis suturalis)</li>
                <li>Tamanduá-da-soja (Sternechus subsignatus)</li>
              </ul>
            </div>
            <div class="col-12 col-lg-5">
              <div class="guide-mini-title">Cultura: Trigo</div>
              <ul class="guide-list">
                <li>Tombamento (Aspergillus spp.)</li>
                <li>Giberela (Fusarium graminearum)</li>
                <li>Fungo-de-armazenamento (Penicillium spp.)</li>
                <li>Tombamento (Pythium spp.)</li>
                <li>Pão-de-galinha (Diloboderus abderus)</li>
              </ul>
            </div>
          </div>
        </div>

        <div class="guide-label">Exemplos para teste (simulação)</div>
        <div class="guide-card">
          <div class="row">
            <div class="col-12 col-lg-6 mb-2 mb-lg-0">
              <div class="guide-mini-title">Responsáveis técnicos</div>
              <ul class="guide-list">
                <li>Eng. Agrônoma Mariana Costa | CPF: 123.456.789-10 | CREA-PR: PR-123456/D | Londrina/PR</li>
                <li>Eng. Agrônomo Carlos Menezes | CPF: 987.654.321-00 | CREA-SC: SC-778899/D | Chapecó/SC</li>
                <li>Eng. Agrônoma Paula Ribeiro | CPF: 741.852.963-00 | CREA-RS: RS-445566/D | Passo Fundo/RS</li>
              </ul>
            </div>
            <div class="col-12 col-lg-6">
              <div class="guide-mini-title">Produtores</div>
              <ul class="guide-list">
                <li>João Pereira da Silva | CPF/CNPJ: 111.222.333-44 | Fazenda Esperança | Londrina/PR</li>
                <li>Agro Norte Paraná Ltda | CPF/CNPJ: 12.345.678/0001-90 | Sítio Santa Luzia | Maringá/PR</li>
                <li>Sementes Alto Vale SC Ltda | CPF/CNPJ: 22.333.444/0001-55 | Fazenda Campo Belo | Xanxerê/SC</li>
                <li>Agro Pampa Sul Ltda | CPF/CNPJ: 33.444.555/0001-66 | Estância Boa Vista | Passo Fundo/RS</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </b-modal>

    <b-modal v-model="showSiagroErrorDetailsModal" title="Detalhes do Erro - Siagro" size="lg" hide-footer>
      <div>
        <div class="mb-2">
          <div><strong>Arquivo:</strong> {{ selectedSiagroErrorArquivo }}</div>
          <div><strong>Enviado em:</strong> {{ selectedSiagroErrorEnviadoEm }}</div>
          <div><strong>Resumo:</strong> {{ selectedSiagroErrorSummary }}</div>
        </div>

        <div v-if="!selectedSiagroErrorRows.length" class="text-muted">
          Nenhum erro detalhado recebido
        </div>

        <b-table
          v-else
          striped
          hover
          responsive="sm"
          bordered
          small
          :items="selectedSiagroErrorRows"
          :fields="siagroErrorDetailFields"
        />

        <div class="d-flex justify-content-end mt-3">
          <button class="btn btn-outline-secondary mr-2" @click="copySiagroErrorsPayload(selectedSiagroErrorPayload)">
            Copiar erros
          </button>
          <button class="btn btn-primary" @click="showSiagroErrorDetailsModal = false">
            Fechar
          </button>
        </div>
      </div>
    </b-modal>

    <b-modal v-model="showGenerateReceitaModal" title="Pré-visualização do Receituário" title-class="font-18" size="xl"
      modal-class="receita-modal" body-class="receita-modal-body" hide-footer>
      <div v-if="selectedPurchaseOrder" class="receita-sheet-wrap">
        <div class="receita-doc">
          <div class="receita-doc-header">
            <div class="doc-brand">
              <img class="doc-brand-logo"
                src="https://res.cloudinary.com/das1rnjvi/image/upload/v1771554621/Captura_de_tela_de_2026-02-19_23-28-44_qucaja.png"
                alt="WebReceita" />
            </div>
            <div class="doc-title">RECEITUÁRIO AGRONÔMICO</div>
            <div class="doc-header-right">
              <div>Receita nº: {{ selectedPurchaseOrder.numero }}</div>
              <div>ART: {{ receituarioPreview.artNumero }}</div>
            </div>
          </div>

          <div class="doc-section-title">RESPONSÁVEL TÉCNICO</div>
          <div class="doc-row doc-row-3">
            <div class="doc-line"><strong>Nome:</strong> {{ receituarioPreview.responsavel }}</div>
            <div class="doc-line"><strong>CPF:</strong> {{ receituarioPreview.cpf }}</div>
            <div class="doc-line"><strong>CREA:</strong> {{ receituarioPreview.crea }}</div>
          </div>

          <div class="doc-section-title">PRODUTOR</div>
          <div class="doc-row doc-row-2">
            <div class="doc-line"><strong>Nome:</strong> {{ receituarioPreview.produtor }}</div>
            <div class="doc-line"><strong>CPF/CNPJ:</strong> {{ receituarioPreview.documentoProdutor }}</div>
          </div>
          <div class="doc-row doc-row-2">
            <div class="doc-line"><strong>Propriedade:</strong> {{ receituarioPreview.localAplicacao }}</div>
            <div class="doc-line"><strong>Município:</strong> {{ receituarioPreview.municipio }}</div>
          </div>
          <div class="doc-line"><strong>Coordenadas Geográficas:</strong> {{ receituarioPreview.coordenadas }}</div>

          <div class="doc-section-title">DIAGNÓSTICO</div>
          <div class="doc-row doc-row-2">
            <div class="doc-line"><strong>Cultura:</strong> {{ receituarioPreview.cultura }}</div>
            <div v-if="isReceituarioTratamentoSementes(receituarioPreview)" class="doc-line">
              <strong>Quantidade:</strong> {{ getQuantidadeSementeLabel(receituarioPreview) }}
            </div>
            <div v-else class="doc-line"><strong>Área (ha):</strong> {{ receituarioPreview.areaHectare }}</div>
          </div>
          <div class="doc-line"><strong>Diagnóstico:</strong> {{ receituarioPreview.diagnostico }}</div>
          <div class="doc-line"><strong>Produto:</strong> {{ receituarioPreview.produto }}</div>
          <div class="doc-row doc-row-3 doc-row-dose">
            <div class="doc-line"><strong>Dose:</strong> {{ receituarioPreview.dose }}</div>
            <div class="doc-line doc-line-nowrap"><strong>Intervalo de Segurança:</strong> {{ formatIntervaloSeguranca(receituarioPreview.intervaloSeguranca) }}
            </div>
            <div class="doc-line doc-line-nowrap"><strong>Nº de Aplicações:</strong> {{ receituarioPreview.aplicacoes }}</div>
          </div>
          <div class="doc-line"><strong>Volume de Calda:</strong> {{ receituarioPreview.volumeCalda }}</div>
          <div class="doc-line"><strong>Obs:</strong> {{ receituarioPreview.observacao }}</div>

          <div class="doc-section-title">DISTÂNCIAS MÍNIMAS PARA A APLICAÇÃO DE AGROTÓXICOS</div>
          <p class="doc-paragraph">
            Art. 1º O uso de agrotóxicos na agropecuária deve ser precedido de receituário agronômico prescrito por
            profissional habilitado, observada a tecnologia de aplicação nele prevista.
          </p>
          <p class="doc-paragraph">
            Art. 2º É vedada a aplicação aérea em áreas situadas a distância mínima de 500m de mananciais de captação,
            núcleos populacionais, escolas e habitações isoladas.
          </p>
          <p class="doc-paragraph">
            Art. 3º Será permitida a aplicação terrestre em relação aos locais do art. 2º quando efetuada por
            atomizadores
            ou canhões, numa distância mínima de 250m.
          </p>
          <div class="doc-footer">
            <p class="doc-paragraph doc-final-text">
              A receita agronômica é a autorização para o uso de agrotóxicos e a exigência de sua apresentação tem por
              objetivos evitar aplicação desnecessária e o uso incorreto de agrotóxicos, mediante prescrição a partir de
              diagnóstico específico, conforme o art. 66, inc. II, do Decreto Federal nº 4.074/2002.
            </p>
            <p class="doc-paragraph doc-warning">
              LEIA ATENTAMENTE O RÓTULO E BULA DO PRODUTO. AGRICULTOR, AGROTÓXICO É VENENO.
            </p>
            <p class="doc-city-date">{{ receituarioPreview.localAssinatura }}</p>

            <div class="doc-signature">
              <div class="doc-sign-col">
                <div class="doc-sign-line"></div>
                <div class="doc-sign-name">{{ receituarioPreview.produtor }}</div>
                <div class="doc-sign-sub">CPF/CNPJ: {{ receituarioPreview.documentoProdutor }}</div>
              </div>
              <div class="doc-sign-col">
                <div class="doc-sign-line"></div>
                <div class="doc-sign-name">{{ receituarioPreview.responsavel }}</div>
                <div class="doc-sign-sub">CPF: {{ receituarioPreview.cpf }} | CFTA: {{ receituarioPreview.crea }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="d-flex justify-content-end mt-3">
        <b-button variant="light" class="mr-2" @click="showGenerateReceitaModal = false">Cancelar</b-button>
        <b-button variant="success" @click="confirmGenerateReceituario">Confirmar geração</b-button>
      </div>
    </b-modal>

    <b-sidebar v-model="showIntegrationSidebar" right shadow backdrop width="430px" title="Central de Geração e Envio">
      <div class="integration-hub">
        <div class="integration-subtitle">
          Gere arquivos para upload manual (SIAGRO e SIG@) e sincronize o webservice do SIGEN+.
        </div>

        <div class="mt-2">
          <b-form-group label="Data Inicial">
            <date-picker v-model="integrationForm.data_inicial" :first-day-of-week="0" format="YYYY-MM-DD"
              value-type="format" lang="pt-br" placeholder="yyyy-mm-dd"></date-picker>
          </b-form-group>
        </div>

        <div>
          <b-form-group label="Data Final">
            <date-picker v-model="integrationForm.data_final" :first-day-of-week="0" format="YYYY-MM-DD"
              value-type="format" lang="pt-br" placeholder="yyyy-mm-dd"></date-picker>
          </b-form-group>
        </div>

        <div>
          <b-form-group label="CPF (somente SIG@)">
            <b-form-input v-model="integrationForm.cpf" placeholder="Ex: 123.456.789-00"></b-form-input>
          </b-form-group>
        </div>

        <div class="d-flex flex-wrap">
          <button class="btn btn-outline-primary mr-2 mb-2" @click="gerarSiagro">
            Gerar SIAGRO
          </button>
          <button class="btn btn-outline-primary mb-2" @click="gerarSiga">
            Gerar SIG@
          </button>
        </div>

        <div class="sync-note mt-2 mb-2">
          SIGEN+ opera em rotina diária via webservice. Use sincronização para atualizar os envios do dia.
        </div>

        <button class="btn btn-success mb-2" @click="sincronizarSigenWebservice">
          Sincronizar SIGEN+
        </button>

        <div class="integration-log mt-3" v-if="integrationLog.length">
          <div class="log-title">Últimas execuções</div>
          <div class="log-item" v-for="log in integrationLog" :key="log.id">
            <span class="log-name">{{ log.nome }}</span>
            <span :class="['log-status', log.status === 'SUCESSO' ? 'ok' : 'warn']">{{ log.status }}</span>
            <span class="log-time">{{ log.horario }}</span>
          </div>
        </div>
      </div>
    </b-sidebar>

    <b-sidebar v-model="showFilterSidebar" right shadow backdrop width="380px" title="Filtros"
      body-class="filter-sidebar-body">
      <div class="filter-sidebar">
        <div class="filter-sidebar-header">
          <div class="filter-sidebar-title">Filtros de Gestão</div>
          <div class="filter-sidebar-subtitle">
            Ajuste os critérios para encontrar rapidamente as receitas que precisam de ação.
          </div>
        </div>

        <div class="filter-panel">
          <b-form-group class="filter-group" label="Situação Geral" label-for="statusFilter">
            <b-form-select id="statusFilter" v-model="filters.status" :options="statusOptions" />
          </b-form-group>

          <b-form-group class="filter-group" label="Órgão" label-for="orgaoFilter">
            <b-form-select id="orgaoFilter" v-model="filters.orgao" :options="orgaoOptions" />
          </b-form-group>

          <b-form-group class="filter-group" label="Período" label-for="periodoFilter">
            <b-form-select id="periodoFilter" v-model="filters.periodo" :options="periodoOptions" />
          </b-form-group>

          <b-form-group class="filter-group" label="Busca" label-for="searchFilter">
            <b-form-input id="searchFilter" v-model="filters.search" placeholder="Número, produtor ou técnico" />
          </b-form-group>

          <b-form-checkbox v-model="filters.somentePendentes" class="mb-0 filter-check">
            Mostrar somente receitas ainda não enviadas
          </b-form-checkbox>
        </div>

        <div class="filter-sidebar-actions">
          <button class="btn btn-outline-secondary action-btn mr-2" @click="clearSearch">Limpar busca</button>
          <button class="btn btn-primary action-btn" @click="showFilterSidebar = false">Aplicar</button>
        </div>
      </div>
    </b-sidebar>

    <loading :active.sync="isLoading" color="#008e75" :can-cancel="false"></loading>
  </component>
</template>

<script>
import Layout from '@layouts/main'
import Loading from 'vue-loading-overlay'
import 'vue-loading-overlay/dist/vue-loading.css'
import DatePicker from 'vue2-datepicker'
import 'vue2-datepicker/locale/pt-br'
import 'vue2-datepicker/index.css'
import reportsService from '@/src/services/reports/reports'
import arquivoService from '@/src/services/arquivo'
import { connectRealtime as connectRealtimeSocket } from '@/src/services/realtime'

const REALTIME_LOCAL_STORAGE_KEY = 'gestaoReceituariosRealtimeData'

export default {
  props: {
    noLayout: {
      type: Boolean,
      default: false,
    },
  },
  components: { Layout, Loading, DatePicker },
  data() {
    return {
      isLoading: false,
      realtimeSocket: null,
      realtimeConnected: false,
      realtimeNotice: null,
      realtimeNoticeTimeoutId: null,
      showFilterSidebar: false,
      showIntegrationSidebar: false,
      showWebhookGuideModal: false,
      showSiagroErrorDetailsModal: false,
      showMarkModal: false,
      showGenerateReceitaModal: false,
      selectedSiagroErrorEvent: null,
      selectedItem: null,
      selectedPurchaseOrder: null,
      selectedChannel: '',
      receituarioPreview: {},
      markForm: {
        protocolo: '',
        observacao: '',
      },
      integrationForm: {
        data_inicial: '',
        data_final: '',
        cpf: '',
      },
      baixaResult: null,
      generationHistory: [],
      purchaseOrderState: {},
      generatedReceitaByOrderId: {},
      generatedArtByOrderId: {},
      generatedDocumentByOrderId: {},
      orphanRealtimeOrders: [],
      integrationLog: [],
      filters: {
        status: 'TODOS',
        orgao: 'TODOS',
        periodo: '30_DIAS',
        search: '',
        somentePendentes: true,
      },
      statusOptions: [
        { value: 'TODOS', text: 'Todos' },
        { value: 'ENVIADA', text: 'Concluída' },
        { value: 'PENDENTE', text: 'Não enviada' },
        { value: 'INCONSISTENTE', text: 'Inconsistente' },
      ],
      orgaoOptions: [
        { value: 'TODOS', text: 'Todos os órgãos' },
        { value: 'SIAGRO', text: 'SIAGRO (PR)' },
        { value: 'SIGEN', text: 'SIGEN+ (SC)' },
        { value: 'SIGA', text: 'SIG@ (RS)' },
      ],
      periodoOptions: [
        { value: '7_DIAS', text: 'Últimos 7 dias' },
        { value: '30_DIAS', text: 'Últimos 30 dias' },
        { value: '90_DIAS', text: 'Últimos 90 dias' },
      ],
      fields: [
        { key: 'numero', label: 'Receita' },
        { key: 'produtor', label: 'Produtor' },
        { key: 'responsavel', label: 'Responsável Técnico' },
        { key: 'estado', label: 'UF' },
        { key: 'siagro', label: 'SIAGRO (PR)' },
        { key: 'sigen', label: 'SIGEN+ (SC)' },
        { key: 'siga', label: 'SIG@ (RS)' },
        { key: 'statusGeral', label: 'Situação Geral' },
      ],
      responsavelSummaryFields: [
        { key: 'responsavel', label: 'Responsável Técnico' },
        { key: 'pendentes', label: 'Emissões Pendentes' },
        { key: 'geradas', label: 'Emissões Geradas' },
        { key: 'total', label: 'Total' },
      ],
      purchaseFields: [
        { key: 'numero', label: 'Receita' },
        { key: 'produtor', label: 'Produtor' },
        { key: 'estado', label: 'UF' },
        { key: 'prioridade', label: 'Prioridade' },
        { key: 'statusPedido', label: 'Status da Geração' },
        { key: 'actions', label: '' },
      ],
      siagroErrorDetailFields: [
        { key: 'linha', label: 'Linha' },
        { key: 'descricaoErro', label: 'Descrição do erro' },
        { key: 'valorEncontrado', label: 'Valor encontrado' },
      ],
      items: [
        {
          id: 1,
          numero: 'RT-260201',
          produtor: 'Sementes Vale Sul',
          responsavel: 'Bruno Nogueira',
          estado: 'PR',
          emissao: '2026-02-17',
          siagro_status: 'PENDENTE',
          sigen_status: 'NAO_APLICAVEL',
          siga_status: 'NAO_APLICAVEL',
          protocolo: '',
          observacao: '',
        },
        {
          id: 2,
          numero: 'RT-260202',
          produtor: 'Campos de Inverno',
          responsavel: 'Elisa Martins',
          estado: 'SC',
          emissao: '2026-02-17',
          siagro_status: 'NAO_APLICAVEL',
          sigen_status: 'PENDENTE',
          siga_status: 'NAO_APLICAVEL',
          protocolo: '',
          observacao: '',
        },
        {
          id: 3,
          numero: 'RT-260203',
          produtor: 'Sementes Horizonte',
          responsavel: 'Felipe Duarte',
          estado: 'RS',
          emissao: '2026-02-16',
          siagro_status: 'NAO_APLICAVEL',
          sigen_status: 'NAO_APLICAVEL',
          siga_status: 'INCONSISTENTE',
          protocolo: '',
          observacao: 'Arquivo com divergência de campos obrigatórios',
        },
        {
          id: 4,
          numero: 'RT-260204',
          produtor: 'Agro Delta PR',
          responsavel: 'Marina Lopes',
          estado: 'PR',
          emissao: '2026-02-16',
          siagro_status: 'ENVIADO',
          sigen_status: 'NAO_APLICAVEL',
          siga_status: 'NAO_APLICAVEL',
          protocolo: 'PR-2026-00912',
          observacao: 'Enviado no fechamento semanal',
        },
        {
          id: 5,
          numero: 'RT-260205',
          produtor: 'Santa Terra Seeds',
          responsavel: 'Carlos Ribeiro',
          estado: 'SC',
          emissao: '2026-02-15',
          siagro_status: 'NAO_APLICAVEL',
          sigen_status: 'ENVIADO',
          siga_status: 'NAO_APLICAVEL',
          protocolo: 'SC-WS-11290',
          observacao: 'Transmitido via webservice',
        },
        {
          id: 6,
          numero: 'RT-260206',
          produtor: 'Pampa Sul',
          responsavel: 'Adriana Faria',
          estado: 'RS',
          emissao: '2026-02-15',
          siagro_status: 'NAO_APLICAVEL',
          sigen_status: 'NAO_APLICAVEL',
          siga_status: 'PENDENTE',
          protocolo: '',
          observacao: '',
        },
      ],
    }
  },
  mounted() {
    this.hydratePersistedRealtimeData()
    this.connectRealtime()
  },
  beforeDestroy() {
    this.disconnectRealtime()
  },
  computed: {
    layoutTag() {
      return this.noLayout ? 'div' : 'Layout'
    },
    filteredItems() {
      const searchText = String(this.filters.search || '').trim().toLowerCase()

      return this.items
        .map((item) => ({
          ...item,
          statusGeral: this.getGeneralStatus(item),
          statusGeralLabel: this.statusLabel(this.getGeneralStatus(item)),
        }))
        .filter((item) => {
          const matchesStatus = this.filters.status === 'TODOS' || item.statusGeral === this.filters.status
          const matchesOrgao = this.matchesOrgaoFilter(item)
          const matchesSearch =
            !searchText ||
            item.numero.toLowerCase().includes(searchText) ||
            item.produtor.toLowerCase().includes(searchText) ||
            item.responsavel.toLowerCase().includes(searchText)
          const matchesPendentes = !this.filters.somentePendentes || item.statusGeral !== 'ENVIADA'

          return matchesStatus && matchesOrgao && matchesSearch && matchesPendentes
        })
    },
    metrics() {
      const total = this.purchaseOrders.length
      const sent = this.purchaseOrders.filter((item) => item.statusPedido === 'Gerado').length
      const pending = this.purchaseOrders.filter((item) => item.statusPedido === 'Pendente').length
      const error = this.purchaseOrders.filter((item) => item.prioridade === 'Alta').length
      return { total, sent, pending, error }
    },
    sentPercent() {
      return this.getPercent(this.metrics.sent)
    },
    pendingPercent() {
      return this.getPercent(this.metrics.pending)
    },
    errorPercent() {
      return this.getPercent(this.metrics.error)
    },
    sentRate() {
      return `${this.sentPercent}%`
    },
    selectedOrgaoLabel() {
      if (this.selectedChannel === 'siagro') return 'SIAGRO (PR)'
      if (this.selectedChannel === 'sigen') return 'SIGEN+ (SC)'
      if (this.selectedChannel === 'siga') return 'SIG@ (RS)'
      return ''
    },
    purchaseOrders() {
      const sequencialPorEstado = {}
      const baseOrders = this.items
        .map((item) => {
          const statusGeral = this.getGeneralStatus(item)
          const shouldShow = statusGeral === 'PENDENTE' || statusGeral === 'INCONSISTENTE'
          if (!shouldShow) return null

          const estado = item.estado || ''
          if (!sequencialPorEstado[estado]) sequencialPorEstado[estado] = 0
          sequencialPorEstado[estado] += 1

          const numeroPadrao = this.formatNumeroReceitaPedido(item, sequencialPorEstado[estado])
          const numeroGerado = this.generatedReceitaByOrderId[item.id]
          const hasPedido = this.purchaseOrderState[item.id] === true
          const prioridade = statusGeral === 'INCONSISTENTE' ? 'Alta' : 'Normal'
          return {
            id: item.id,
            numero: numeroGerado || numeroPadrao,
            produtor: item.produtor,
            responsavel: item.responsavel || 'Não informado',
            estado: item.estado,
            prioridade,
            statusPedido: hasPedido ? 'Gerado' : 'Pendente',
          }
        })
        .filter((item) => item !== null)
        .slice(0, 1)

      const merged = [...baseOrders]
      this.orphanRealtimeOrders.forEach((order) => {
        if (!order || !order.numero) return
        if (!merged.find((item) => item.numero === order.numero)) {
          const isGenerated = this.purchaseOrderState[order.id] === true
          merged.push({
            ...order,
            statusPedido: isGenerated ? 'Gerado' : 'Pendente',
          })
        }
      })

      return merged
    },
    purchaseSummary() {
      const pending = this.purchaseOrders.filter((item) => item.statusPedido === 'Pendente').length
      const generated = this.purchaseOrders.filter((item) => item.statusPedido === 'Gerado').length
      const highPriority = this.purchaseOrders.filter((item) => item.prioridade === 'Alta').length
      return { pending, generated, highPriority }
    },
    responsavelSummaryItems() {
      const grouped = {}

      this.purchaseOrders.forEach((item) => {
        const responsavel = item.responsavel || 'Não informado'
        if (!grouped[responsavel]) {
          grouped[responsavel] = {
            responsavel,
            pendentes: 0,
            geradas: 0,
            total: 0,
          }
        }

        grouped[responsavel].total += 1
        if (item.statusPedido === 'Gerado') {
          grouped[responsavel].geradas += 1
        } else {
          grouped[responsavel].pendentes += 1
        }
      })

      return Object.keys(grouped)
        .map((key) => grouped[key])
        .sort((a, b) => {
          if (b.pendentes !== a.pendentes) return b.pendentes - a.pendentes
          return a.responsavel.localeCompare(b.responsavel)
        })
    },
    webhookGuideUrl() {
      const base = String(process.env.VUE_APP_API_RECEITUARIO || '').trim()
      if (!base) return 'https://seu-backend/webreceita/api/v1/webhooks/sementesbortoluzzi'
      return `${base.replace(/\/$/, '')}/webhooks/sementesbortoluzzi`
    },
    webhookPayloadExample() {
      return JSON.stringify(
        {
          companyId: '169',
          data: {
            agronomoNome: 'Eng. Agrônoma Mariana Costa',
            produtorNome: 'João Pereira da Silva',
            produtoNome: 'STANDAK TOP',
            cultura: 'Soja',
            alvo: 'Fungo-de-armazenamento (Aspergillus flavus)',
            quantidadeSementeKg: 2500,
          },
        },
        null,
        2
      )
    },
    siagroLatestErrorEvent() {
      const moduleState = this.$store && this.$store.state ? this.$store.state.siagroErrors : null
      if (!moduleState || !Array.isArray(moduleState.events) || !moduleState.events.length) return null
      return moduleState.events[0]
    },
    historyTimeline() {
      const generationEvents = (this.generationHistory || []).map((entry) => {
        const ts = Number(String(entry.id || '').split('-')[0]) || 0
        return {
          uid: `g-${entry.id}`,
          kind: 'GERACAO',
          ts,
          title: `${entry.orgao} | ${entry.tipoArquivo}`,
          subtitle: `${entry.dataInicial} até ${entry.dataFinal}`,
          meta: `${entry.geradoEm} | ${entry.fileName}`,
          raw: entry,
        }
      })

      const moduleState = this.$store && this.$store.state ? this.$store.state.siagroErrors : null
      const siagroEvents = moduleState && Array.isArray(moduleState.events) ? moduleState.events : []
      const siagroErrorEvents = siagroEvents.map((evt, index) => {
        const payload = evt && evt.payload ? evt.payload : {}
        const sentDate = payload.enviadoEm ? new Date(payload.enviadoEm) : null
        const evtDate = evt && evt.ts ? new Date(evt.ts) : null
        const ts = sentDate && !Number.isNaN(sentDate.getTime())
          ? sentDate.getTime()
          : evtDate && !Number.isNaN(evtDate.getTime())
            ? evtDate.getTime()
            : 0
        const when = ts ? this.formatDateTime(new Date(ts)) : '-'
        const arquivo = payload.arquivo || '-'
        const qtd = Number(payload.quantidadeErros || 0)
        return {
          uid: `s-${index}-${ts}`,
          kind: 'SIAGRO_ERRO',
          ts,
          title: `SIAGRO | Validação de upload (${qtd})`,
          subtitle: payload.summary || 'Erro de validação no upload do receituário',
          meta: `${when} | Arquivo: ${arquivo}`,
          raw: evt,
        }
      })

      return [...generationEvents, ...siagroErrorEvents].sort((a, b) => b.ts - a.ts)
    },
    selectedSiagroErrorPayload() {
      return this.selectedSiagroErrorEvent && this.selectedSiagroErrorEvent.payload
        ? this.selectedSiagroErrorEvent.payload
        : {}
    },
    selectedSiagroErrorRows() {
      const payload = this.selectedSiagroErrorPayload || {}
      const errors = Array.isArray(payload.erros) ? payload.erros : []
      return errors.map((error) => ({
        linha: error.line || error.numeroLinha || '-',
        descricaoErro: error.descricaoErro || '-',
        valorEncontrado: this.maskDocumentoInText(error.valorEncontrado || '-'),
      }))
    },
    selectedSiagroErrorArquivo() {
      const payload = this.selectedSiagroErrorPayload || {}
      return payload.arquivo || '-'
    },
    selectedSiagroErrorEnviadoEm() {
      const payload = this.selectedSiagroErrorPayload || {}
      const value = payload.enviadoEm || ''
      if (!value) return '-'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return value
      return this.formatDateTime(date)
    },
    selectedSiagroErrorSummary() {
      const payload = this.selectedSiagroErrorPayload || {}
      return payload.summary || 'Erro de validação no upload do receituário'
    },
  },
  methods: {
    hydratePersistedRealtimeData() {
      try {
        const raw = window.localStorage.getItem(REALTIME_LOCAL_STORAGE_KEY)
        if (!raw) return
        const parsed = JSON.parse(raw)
        if (!parsed || typeof parsed !== 'object') return

        this.purchaseOrderState = parsed.purchaseOrderState || {}
        this.generatedReceitaByOrderId = parsed.generatedReceitaByOrderId || {}
        this.generatedArtByOrderId = parsed.generatedArtByOrderId || {}
        this.generatedDocumentByOrderId = parsed.generatedDocumentByOrderId || {}
        this.orphanRealtimeOrders = Array.isArray(parsed.orphanRealtimeOrders) ? parsed.orphanRealtimeOrders : []
      } catch (error) {
        this.addIntegrationLog('Falha ao restaurar dados locais do realtime', 'ALERTA')
      }
    },
    persistRealtimeData() {
      try {
        const payload = {
          purchaseOrderState: this.purchaseOrderState,
          generatedReceitaByOrderId: this.generatedReceitaByOrderId,
          generatedArtByOrderId: this.generatedArtByOrderId,
          generatedDocumentByOrderId: this.generatedDocumentByOrderId,
          orphanRealtimeOrders: this.orphanRealtimeOrders,
        }
        window.localStorage.setItem(REALTIME_LOCAL_STORAGE_KEY, JSON.stringify(payload))
      } catch (error) {
        this.addIntegrationLog('Falha ao salvar dados locais do realtime', 'ALERTA')
      }
    },
    isSiagroUploadValidationErrorEvent(evt) {
      if (!evt || typeof evt !== 'object') return false
      if (evt.type !== 'siagro.upload.validation.error') return false
      if (!evt.payload || typeof evt.payload !== 'object') return false
      return true
    },
    handleSiagroUploadValidationError(evt) {
      if (!this.isSiagroUploadValidationErrorEvent(evt)) return false

      if (this.$store && this.$store.commit) {
        this.$store.commit('siagroErrors/ADD_ERROR_EVENT', evt)
      }

      const summary = evt.payload && evt.payload.summary
        ? evt.payload.summary
        : 'Erro de validação no upload do Siagro.'

      this.$bvToast.toast(summary, {
        variant: 'warning',
        solid: false,
        noCloseButton: true,
        toaster: 'b-toaster-top-center',
      })

      this.addIntegrationLog('Siagro: erro de validação de upload', 'ALERTA')
      return true
    },
    openSiagroErrorDetails(evt) {
      if (!evt || !evt.payload) return
      this.selectedSiagroErrorEvent = evt
      this.showSiagroErrorDetailsModal = true
    },
    maskCpfValue(value) {
      const digits = String(value || '').replace(/\D/g, '')
      if (digits.length !== 11) return String(value || '')
      return '***.***.***-**'
    },
    maskDocumentoInText(text) {
      const raw = String(text || '')
      return raw.replace(/\d{11}/g, (cpf) => this.maskCpfValue(cpf))
    },
    async copySiagroErrorsPayload(payloadInput) {
      const payload = payloadInput || (this.siagroLatestErrorEvent && this.siagroLatestErrorEvent.payload) || {}
      const text = JSON.stringify(payload, null, 2)

      try {
        if (navigator && navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
          await navigator.clipboard.writeText(text)
        } else {
          const textarea = document.createElement('textarea')
          textarea.value = text
          textarea.setAttribute('readonly', 'readonly')
          textarea.style.position = 'absolute'
          textarea.style.left = '-9999px'
          document.body.appendChild(textarea)
          textarea.select()
          document.execCommand('copy')
          document.body.removeChild(textarea)
        }

        this.$bvToast.toast('Payload de erros copiado para a área de transferência.', {
          variant: 'success',
          solid: false,
          noCloseButton: true,
          toaster: 'b-toaster-top-center',
        })
      } catch (error) {
        this.$bvToast.toast('Não foi possível copiar os erros.', {
          variant: 'danger',
          solid: false,
          noCloseButton: true,
          toaster: 'b-toaster-top-center',
        })
      }
    },
    getCurrentUser() {
      return this.$store && this.$store.state && this.$store.state.auth
        ? this.$store.state.auth.currentUser || {}
        : {}
    },
    getEmpresaId(user) {
      if (!user) return null
      const candidates = [
        user.id_empresa_sistema,
        user.id_empresa,
        user.idEmpresa,
        user.empresa_id,
        user.idEmpresaSistema,
        user.empresa && user.empresa.id_empresa_sistema,
        user.empresa && user.empresa.id_empresa,
        user.empresa && user.empresa.id,
      ]

      for (let i = 0; i < candidates.length; i += 1) {
        const parsed = Number(candidates[i])
        if (!Number.isNaN(parsed) && parsed > 0) return parsed
      }

      if (window.localStorage.getItem('modo_demo_169') === '1') return 169

      return null
    },
    getUserId(user) {
      if (!user) return null
      const candidates = [
        user.id_usuario,
        user.idUsuario,
        user.user_id,
        user.id_user,
        user.id,
        user.usuario_id,
      ]

      for (let i = 0; i < candidates.length; i += 1) {
        const parsed = Number(candidates[i])
        if (!Number.isNaN(parsed) && parsed > 0) return parsed
      }
      return null
    },
    isEmpresa169() {
      if (window.localStorage.getItem('modo_demo_169') === '1') return true
      const user = this.getCurrentUser()
      return this.getEmpresaId(user) === 169
    },
    showDemoGenerationNotEnabledMessage() {
      this.$bvToast.toast(
        'A geração de arquivos nesta versão de demonstração não está habilitada. Para liberar, solicite mais informações ao suporte da WebReceita.',
        {
          variant: 'warning',
          solid: false,
          noCloseButton: true,
          toaster: 'b-toaster-top-center',
        }
      )
    },
    getRealtimeBaseUrl() {
      const sanitizeBase = (raw) => {
        if (!raw) return ''
        return String(raw)
          .trim()
          .replace(/\/webreceita\/api\/v1\/?$/i, '')
          .replace(/\/realtime\/?$/i, '')
          .replace(/\/$/, '')
      }

      const explicitUrl = sanitizeBase(process.env.VUE_APP_REALTIME_URL || '')
      if (explicitUrl) return explicitUrl

      const apiUrl = String(process.env.VUE_APP_API_RECEITUARIO || '').trim()
      if (apiUrl) {
        return sanitizeBase(apiUrl)
      }

      return window.location.origin
    },
    async connectRealtime() {
      const user = this.getCurrentUser()
      const companyId = this.getEmpresaId(user)
      const userId = this.getUserId(user)
      if (!companyId && !userId) {
        this.addIntegrationLog('Realtime sem companyId/userId (conectando sem salas)', 'ALERTA')
      }
     
      try {
        const baseUrl = this.getRealtimeBaseUrl()
        const realtimePath = String(process.env.VUE_APP_REALTIME_PATH || '/socket.io').trim()

        this.realtimeSocket = connectRealtimeSocket({
          baseUrl,
          path: realtimePath || '/socket.io',
          companyId,
          userId,
        })

        this.realtimeSocket.on('connect', () => {
          this.realtimeConnected = true
          this.addIntegrationLog(
            `Realtime conectado (${baseUrl}/realtime | ${realtimePath || '/socket.io'})`,
            'SUCESSO'
          )
        })

        this.realtimeSocket.on('disconnect', () => {
          this.realtimeConnected = false
          this.addIntegrationLog('Realtime desconectado', 'ALERTA')
        })

        this.realtimeSocket.on('connect_error', (error) => {
          this.realtimeConnected = false
          const reason = error && error.message ? ` (${error.message})` : ''
          this.addIntegrationLog(`Erro de conexão realtime${reason}`, 'ALERTA')
        })

        this.realtimeSocket.on('event', (evt) => {
          this.handleRealtimeEvent(evt, 'event')
        })
        this.realtimeSocket.on('message', (evt) => {
          this.handleRealtimeEvent(evt, 'message')
        })
        this.realtimeSocket.on('notification', (evt) => {
          this.handleRealtimeEvent(evt, 'notification')
        })
        this.realtimeSocket.on('realtime', (evt) => {
          this.handleRealtimeEvent(evt, 'realtime')
        })
      } catch (error) {
        this.realtimeConnected = false
        this.addIntegrationLog('Falha ao carregar realtime', 'ALERTA')
      }
    },
    disconnectRealtime() {
      if (this.realtimeNoticeTimeoutId) {
        clearTimeout(this.realtimeNoticeTimeoutId)
        this.realtimeNoticeTimeoutId = null
      }

      if (!this.realtimeSocket) return

      this.realtimeSocket.off('connect')
      this.realtimeSocket.off('disconnect')
      this.realtimeSocket.off('connect_error')
      this.realtimeSocket.off('event')
      this.realtimeSocket.off('message')
      this.realtimeSocket.off('notification')
      this.realtimeSocket.off('realtime')
      this.realtimeSocket.disconnect()
      this.realtimeSocket = null
      this.realtimeConnected = false
    },
    normalizeRealtimeStatus(status) {
      const normalized = String(status || '').trim().toUpperCase()
      if (normalized === 'ENVIADO' || normalized === 'ENVIADA') return 'ENVIADO'
      if (normalized === 'INCONSISTENTE' || normalized === 'ERRO' || normalized === 'ERROR') return 'INCONSISTENTE'
      if (normalized === 'PENDENTE') return 'PENDENTE'
      return ''
    },
    findItemByRealtimeNumero(numeroReceita) {
      if (!numeroReceita) return null
      const normalized = String(numeroReceita).replace(/^RT-/, '')
      return this.items.find((item) => {
        const num = String(item.numero || '').replace(/^RT-/, '')
        return num === normalized
      })
    },
    applyRealtimeUpdate(evt) {
      const numeroReceita = evt.numeroReceita || evt.numero || evt.receitaNumero || evt.receita
      const item = this.findItemByRealtimeNumero(numeroReceita)
      if (!item) return

      const orgaoRaw = String(evt.orgao || evt.channel || evt.target || '').toUpperCase()
      const status = this.normalizeRealtimeStatus(evt.status || evt.situacao || evt.state)
      if (!status) return

      if (orgaoRaw === 'SIAGRO' || orgaoRaw === 'PR') item.siagro_status = status
      if (orgaoRaw === 'SIGEN' || orgaoRaw === 'SIGEN+' || orgaoRaw === 'SC') item.sigen_status = status
      if (orgaoRaw === 'SIGA' || orgaoRaw === 'SIG@' || orgaoRaw === 'RS') item.siga_status = status
    },
    inferEstadoFromMunicipio(municipio) {
      const text = String(municipio || '').toUpperCase()
      if (text.includes('/PR') || text.endsWith('PR')) return 'PR'
      if (text.includes('/SC') || text.endsWith('SC')) return 'SC'
      if (text.includes('/RS') || text.endsWith('RS')) return 'RS'
      return '--'
    },
    isReceituarioTratamentoSementes(preview) {
      const data = preview || {}
      if (data.isTratamentoSementes) return true
      if (String(data.quantidadeSementeKg || '').trim()) return true

      const modalidadeTexto = String(data.modalidadeAplicacao || '').toLowerCase()
      const diagnosticoTexto = String(data.diagnostico || '').toLowerCase()
      const doseTexto = String(data.dose || '').toLowerCase()
      const observacaoTexto = String(data.observacao || '').toLowerCase()
      const volumeCaldaTexto = String(data.volumeCalda || '').toLowerCase()

      if (modalidadeTexto.includes('tratamento de sementes')) return true
      if (diagnosticoTexto.includes('tratamento de sementes')) return true
      if (observacaoTexto.includes('tratamento de sementes')) return true
      if (doseTexto.includes('kg de sementes')) return true
      if (diagnosticoTexto.includes('quantidade de sementes')) return true
      if (volumeCaldaTexto.includes('tratamento de sementes')) return true
      if (diagnosticoTexto.includes('sementes')) return true
      if (observacaoTexto.includes('sementes')) return true
      return false
    },
    getQuantidadeSementeLabel(preview) {
      const data = preview || {}
      const explicit = String(data.quantidadeSementeKg || '').trim()
      if (explicit) return explicit

      const diagnosticoTexto = String(data.diagnostico || '')
      const match = diagnosticoTexto.match(/quantidade\s+de\s+sementes[:\s]*([\d.,]+)\s*kg/i)
      if (match && match[1]) return `${match[1]} kg de sementes`
      return 'Quantidade não informada'
    },
    formatIntervaloSeguranca(value) {
      const raw = String(value === null || value === undefined ? '' : value).trim()
      if (!raw) return 'N.E'

      const normalized = raw.toLowerCase().replace(/\s+/g, '')
      if (normalized === '0' || normalized === '0dia' || normalized === '0dias') return 'N.E'

      const numeric = Number(String(raw).replace(',', '.'))
      if (!Number.isNaN(numeric) && numeric === 0) return 'N.E'
      return raw
    },
    mapRealtimeDocumentToPreview(document) {
      if (!document) return null

      const responsavel = document.responsavelTecnico || {}
      const produtor = document.produtor || {}
      const diagnostico = document.diagnostico || {}
      const produto = diagnostico.produto || {}
      const dose = diagnostico.dose || {}
      const volumeCalda = diagnostico.volumeCalda || {}
      const tratamentoSementes = diagnostico.tratamentoSementes || {}
      const diagnosticoTexto = String(diagnostico.diagnosticoTexto || '')
      const doseTexto = String(dose.texto || '')
      const observacaoTexto = String(diagnostico.observacao || '')
      const modalidadeAplicacao =
        document.modalidadeAplicacao ||
        diagnostico.modalidadeAplicacao ||
        ''
      const quantidadeSementeRaw =
        tratamentoSementes.quantidadeSementeKg ||
        document.quantidadeSementeKg ||
        diagnostico.quantidadeSementeKg ||
        document.quantidade ||
        ''
      const quantidadeFromTextMatch = diagnosticoTexto.match(/quantidade\s+de\s+sementes[:\s]*([\d.,]+)/i)
      const quantidadeFromText = quantidadeFromTextMatch && quantidadeFromTextMatch[1] ? quantidadeFromTextMatch[1] : ''
      const quantidadeSementeKg = quantidadeSementeRaw || quantidadeFromText
      const isTratamentoSementes =
        String(modalidadeAplicacao).toLowerCase().includes('tratamento de sementes') ||
        diagnosticoTexto.toLowerCase().includes('tratamento de sementes') ||
        doseTexto.toLowerCase().includes('kg de sementes') ||
        observacaoTexto.toLowerCase().includes('tratamento de sementes') ||
        !!quantidadeSementeKg
      const quantidadeSementeLabel =
        quantidadeSementeKg && /[a-zA-Z]/.test(String(quantidadeSementeKg))
          ? String(quantidadeSementeKg)
          : quantidadeSementeKg
            ? `${quantidadeSementeKg} kg de sementes`
            : ''
      const doseTratamento = tratamentoSementes.doseMlPor100Kg
        ? `${tratamentoSementes.doseMlPor100Kg} mL/100 kg de sementes`
        : ''
      const doseTotalTratamento = tratamentoSementes.doseTotalMl
        ? `Dose total estimada: ${Number(tratamentoSementes.doseTotalMl / 1000).toFixed(2)} L`
        : ''
      const volumeTratamento = tratamentoSementes.volumeCaldaLPor100Kg
        ? `${tratamentoSementes.volumeCaldaLPor100Kg} L/100 kg de sementes`
        : ''
      const volumeTotalTratamento = tratamentoSementes.volumeTotalCaldaMl
        ? `Volume total estimado: ${(tratamentoSementes.volumeTotalCaldaMl / 1000).toFixed(2)} L`
        : ''
      const nomeComercial = String(produto.nomeComercial || '').trim()
      const ingredienteAtivo = String(produto.ingredienteAtivo || '').trim()
      const concentracao = String(produto.concentracao || '').trim()
      let produtoLabel = ''

      if (nomeComercial && ingredienteAtivo) {
        produtoLabel = `${nomeComercial} (${ingredienteAtivo})`
      } else if (nomeComercial && concentracao) {
        produtoLabel = `${nomeComercial} (${concentracao})`
      } else {
        produtoLabel = nomeComercial
      }

      return {
        responsavel: responsavel.nome || 'Responsável técnico não informado',
        cpf: responsavel.cpf || '',
        crea: responsavel.registroProfissional || '',
        produtor: produtor.nome || 'Produtor não informado',
        documentoProdutor: produtor.cpfCnpj || '',
        municipio: produtor.municipio || '',
        localAplicacao: produtor.propriedade || '',
        coordenadas: produtor.coordenadasGeograficas || '',
        areaHectare: diagnostico.areaHa || '',
        quantidadeSementeKg: quantidadeSementeLabel,
        isTratamentoSementes,
        modalidadeAplicacao,
        cultura: diagnostico.cultura || '',
        diagnostico: diagnostico.diagnosticoTexto || '',
        produto: produtoLabel,
        dose: doseTratamento || dose.texto || '',
        volumeCalda: volumeTratamento || volumeCalda.texto || '',
        aplicacoes: diagnostico.numeroAplicacoes || '',
        intervaloSeguranca: diagnostico.intervaloSegurancaDias
          ? `${diagnostico.intervaloSegurancaDias} dias`
          : '',
        observacao: [diagnostico.observacao || '', doseTotalTratamento, volumeTotalTratamento]
          .filter((item) => String(item || '').trim())
          .join(' '),
        artNumero: document.artNumero || '',
        localAssinatura: document.cidadeDataLinha || '',
      }
    },
    upsertRealtimeOrphanOrder(document) {
      const numero = document && document.receitaNumero ? String(document.receitaNumero) : ''
      if (!numero) return

      const id = `rt-${numero}`
      const estado = this.inferEstadoFromMunicipio(document.produtor && document.produtor.municipio)
      const produtor = (document.produtor && document.produtor.nome) || 'Produtor'
      const responsavel =
        (document.responsavelTecnico && document.responsavelTecnico.nome) || 'Não informado'

      this.$set(this.purchaseOrderState, id, false)
      this.$set(this.generatedReceitaByOrderId, id, numero)
      if (document.artNumero) this.$set(this.generatedArtByOrderId, id, String(document.artNumero))
      this.$set(this.generatedDocumentByOrderId, id, document)

      const next = this.orphanRealtimeOrders.filter((item) => item.id !== id)
      next.unshift({
        id,
        numero,
        produtor,
        responsavel,
        estado,
        prioridade: 'Normal',
        statusPedido: 'Pendente',
      })
      this.orphanRealtimeOrders = next.slice(0, 20)
      this.persistRealtimeData()
    },
    applyDemoReceituarioReady(evt) {
      if (!evt || evt.type !== 'demo.receituario.ready') return
      const document = evt.payload && evt.payload.document ? evt.payload.document : null
      if (!document) return

      const generatedNumero = document.receitaNumero ? String(document.receitaNumero) : ''
      const generatedArt = document.artNumero ? String(document.artNumero) : ''
      const targetOrderId = this.selectedPurchaseOrder && this.selectedPurchaseOrder.id ? this.selectedPurchaseOrder.id : null

      if (targetOrderId !== null && targetOrderId !== undefined) {
        if (generatedNumero) this.$set(this.generatedReceitaByOrderId, targetOrderId, generatedNumero)
        if (generatedArt) this.$set(this.generatedArtByOrderId, targetOrderId, generatedArt)
        this.$set(this.generatedDocumentByOrderId, targetOrderId, document)
        this.$set(this.purchaseOrderState, targetOrderId, false)

        if (this.selectedPurchaseOrder) {
          this.selectedPurchaseOrder = {
            ...this.selectedPurchaseOrder,
            numero: generatedNumero || this.selectedPurchaseOrder.numero,
            statusPedido: 'Pendente',
          }
        }

        const mappedPreview = this.mapRealtimeDocumentToPreview(document)
        if (mappedPreview) this.receituarioPreview = mappedPreview
        this.persistRealtimeData()
        return
      }

      this.upsertRealtimeOrphanOrder(document)
    },
    handleRealtimeEvent(evt, channelName) {
      if (!evt) return

      if (this.handleSiagroUploadValidationError(evt)) {
        return
      }

      const eventName = evt.type || evt.event || channelName || 'Atualização recebida'
      if (process.env.NODE_ENV !== 'production') {
        console.log('[realtime]', channelName || 'event', evt)
      }
      this.applyDemoReceituarioReady(evt)
      this.addIntegrationLog(`Realtime: ${eventName}`, 'SUCESSO')
      this.applyRealtimeUpdate(evt)
      this.showRealtimeNotice(eventName)
    },
    showRealtimeNotice(eventName) {
      this.realtimeNotice = {
        message: eventName,
        time: this.formatDateTime(new Date()),
      }

      if (this.realtimeNoticeTimeoutId) {
        clearTimeout(this.realtimeNoticeTimeoutId)
      }

      this.realtimeNoticeTimeoutId = setTimeout(() => {
        this.realtimeNotice = null
        this.realtimeNoticeTimeoutId = null
      }, 8000)
    },
    matchesOrgaoFilter(item) {
      if (this.filters.orgao === 'TODOS') return true
      if (this.filters.orgao === 'SIAGRO') return item.estado === 'PR'
      if (this.filters.orgao === 'SIGEN') return item.estado === 'SC'
      if (this.filters.orgao === 'SIGA') return item.estado === 'RS'
      return true
    },
    getRequiredChannel(item) {
      if (item.estado === 'PR') return 'siagro'
      if (item.estado === 'SC') return 'sigen'
      if (item.estado === 'RS') return 'siga'
      return ''
    },
    getGeneralStatus(item) {
      const channel = this.getRequiredChannel(item)
      const channelStatus = item[`${channel}_status`] || 'PENDENTE'

      if (channelStatus === 'INCONSISTENTE') return 'INCONSISTENTE'
      if (channelStatus === 'ENVIADO') return 'ENVIADA'
      return 'PENDENTE'
    },
    getPercent(value) {
      if (!this.metrics.total) return 0
      return Math.round((value / this.metrics.total) * 100)
    },
    statusLabel(status) {
      if (status === 'ENVIADA' || status === 'ENVIADO') return 'Enviado'
      if (status === 'PENDENTE') return 'Pendente'
      if (status === 'INCONSISTENTE') return 'Inconsistente'
      if (status === 'NAO_APLICAVEL') return 'N/A'
      return status
    },
    statusBadgeClass(status) {
      if (status === 'ENVIADA' || status === 'ENVIADO') return 'badge-status badge-success'
      if (status === 'PENDENTE') return 'badge-status badge-warning'
      if (status === 'INCONSISTENTE') return 'badge-status badge-danger'
      return 'badge-status badge-neutral'
    },
    clearSearch() {
      this.filters.search = ''
    },
    clearPurchaseOrdersToDemo() {
      this.purchaseOrderState = {}
      this.generatedReceitaByOrderId = {}
      this.generatedArtByOrderId = {}
      this.generatedDocumentByOrderId = {}
      this.orphanRealtimeOrders = []
      this.selectedPurchaseOrder = null
      this.showGenerateReceitaModal = false
      this.persistRealtimeData()

      this.$bvToast.toast('Integrações limpas. Apenas o registro inicial de demonstração foi mantido.', {
        variant: 'warning',
        solid: false,
        noCloseButton: true,
        toaster: 'b-toaster-top-center',
      })
    },
    handlePedidoCompraAction(order) {
      if (!order) return

      if (order.statusPedido === 'Gerado') {
        this.$set(this.purchaseOrderState, order.id, false)
        this.persistRealtimeData()
        if (this.selectedPurchaseOrder && this.selectedPurchaseOrder.id === order.id) {
          this.selectedPurchaseOrder = {
            ...this.selectedPurchaseOrder,
            statusPedido: 'Pendente',
          }
        }
        this.$bvToast.toast(`Geração reaberta para a receita ${order.numero}.`, {
          variant: 'warning',
          solid: false,
          noCloseButton: true,
          toaster: 'b-toaster-top-center',
        })
        return
      }

      const generatedDocument = this.generatedDocumentByOrderId[order.id]
      const generatedNumero = this.generatedReceitaByOrderId[order.id]
      const generatedArt = this.generatedArtByOrderId[order.id]

      this.selectedPurchaseOrder = {
        ...order,
        numero: generatedNumero || order.numero,
      }

      if (generatedDocument) {
        const previewFromDocument = this.mapRealtimeDocumentToPreview(generatedDocument)
        if (previewFromDocument) {
          this.receituarioPreview = {
            ...previewFromDocument,
            artNumero: generatedArt || previewFromDocument.artNumero,
          }
          this.showGenerateReceitaModal = true
          return
        }
      }

      const baseItem = this.items.find((item) => item.id === order.id)
      if (!baseItem) {
        this.receituarioPreview = {
          responsavel: 'Responsável técnico não informado',
          cpf: '',
          crea: '',
          produtor: order.produtor || 'Produtor não informado',
          documentoProdutor: '',
          municipio: order.estado || '',
          localAplicacao: '',
          coordenadas: '',
          areaHectare: '',
          quantidadeSementeKg: '',
          isTratamentoSementes: false,
          cultura: '',
          diagnostico: '',
          produto: '',
          dose: '',
          volumeCalda: '',
          aplicacoes: '',
          intervaloSeguranca: '',
          observacao: 'Aguardando retorno detalhado para montar o receituário completo.',
          artNumero: generatedArt || `BR${this.generateRandomNumericString(10)}`,
          localAssinatura: '',
        }
        this.showGenerateReceitaModal = true
        return
      }

      this.receituarioPreview = this.buildReceituarioPreview(baseItem)
      if (generatedArt) this.receituarioPreview.artNumero = generatedArt
      this.showGenerateReceitaModal = true
    },
    formatNumeroReceitaPedido(item, sequencialEstado) {
      if (item.estado === 'SC') {
        return `SC42261988${String(sequencialEstado).padStart(6, '0')}`
      }

      if (item.estado === 'RS') {
        return String(2600000 + sequencialEstado)
      }

      return String(item.numero || '').replace(/^RT-/, '')
    },
    generateRandomNumericString(length) {
      let value = ''
      for (let i = 0; i < length; i += 1) {
        value += String(Math.floor(Math.random() * 10))
      }
      return value
    },
    buildReceituarioPreview(item) {
      const now = new Date()
      const dataExtenso = now.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
      })
      const municipio = item.estado === 'PR' ? 'Curitiba/PR' : item.estado === 'SC' ? 'Chapecó/SC' : 'Passo Fundo/RS'
      const localAssinatura = `${municipio} ${dataExtenso}`

      return {
        responsavel: item.responsavel || 'Responsável técnico não informado',
        cpf: '000.000.000-00',
        crea: '4569812545',
        produtor: item.produtor,
        documentoProdutor: '77.751.780/0001-36',
        municipio,
        localAplicacao: 'Fazenda Esperança',
        coordenadas: `-${Math.floor(Math.random() * 10 + 20)}°'${Math.floor(Math.random() * 59)}"`,
        areaHectare: item.estado === 'PR' ? '1.33' : item.estado === 'SC' ? '2.10' : '1.85',
        quantidadeSementeKg: '',
        isTratamentoSementes: false,
        cultura: item.estado === 'RS' ? 'Trigo' : item.estado === 'SC' ? 'Milho' : 'Soja',
        diagnostico: 'Infestação inicial detectada em vistoria técnica',
        produto: 'Adver 240 SC (Clorfenapir - 24%)',
        dose: '0.75 L/ha',
        volumeCalda: '400 L/ha',
        aplicacoes: '1',
        intervaloSeguranca: '7 dias',
        observacao: `Emitido para validação operacional em ${dataExtenso}.`,
        artNumero: `BR${this.generateRandomNumericString(10)}`,
        localAssinatura,
      }
    },
    confirmGenerateReceituario() {
      if (!this.selectedPurchaseOrder) return

      this.$set(this.purchaseOrderState, this.selectedPurchaseOrder.id, true)
      this.persistRealtimeData()
      this.selectedPurchaseOrder = {
        ...this.selectedPurchaseOrder,
        statusPedido: 'Gerado',
      }
      this.showGenerateReceitaModal = false

      this.$bvToast.toast(`Receituário gerado para a receita ${this.selectedPurchaseOrder.numero}.`, {
        variant: 'success',
        solid: false,
        noCloseButton: true,
        toaster: 'b-toaster-top-center',
      })
    },
    findLabel(options, value) {
      const found = options.find((option) => option.value === value)
      return found ? found.text : value
    },
    canMarkAsSent(item, channel) {
      const field = `${channel}_status`
      return item[field] !== 'NAO_APLICAVEL' && item[field] !== 'ENVIADO'
    },
    openMarkModal(item, channel) {
      this.selectedItem = item
      this.selectedChannel = channel
      this.markForm = {
        protocolo: item.protocolo || '',
        observacao: item.observacao || '',
      }
      this.showMarkModal = true
    },
    formatDateTime(date) {
      const pad = (value) => String(value).padStart(2, '0')
      return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} ${pad(
        date.getHours()
      )}:${pad(date.getMinutes())}`
    },
    confirmMarkAsSent() {
      if (!this.selectedItem || !this.selectedChannel) return

      const now = this.formatDateTime(new Date())
      this.selectedItem[`${this.selectedChannel}_status`] = 'ENVIADO'
      this.selectedItem.protocolo = this.markForm.protocolo || this.selectedItem.protocolo
      this.selectedItem.observacao = this.markForm.observacao || `Enviado manualmente em ${now}`
      this.showMarkModal = false

      this.$bvToast.toast('Receita marcada como enviada com sucesso.', {
        variant: 'success',
        solid: false,
        noCloseButton: true,
        toaster: 'b-toaster-top-center',
      })
    },
    isValidPeriod() {
      if (!this.integrationForm.data_inicial || !this.integrationForm.data_final) {
        this.$bvToast.toast('Informe data inicial e final para executar a rotina.', {
          variant: 'warning',
          solid: false,
          noCloseButton: true,
          toaster: 'b-toaster-top-center',
        })
        return false
      }

      const start = new Date(`${this.integrationForm.data_inicial}T00:00:00`)
      const end = new Date(`${this.integrationForm.data_final}T00:00:00`)

      if (start.getTime() > end.getTime()) {
        this.$bvToast.toast('A data inicial deve ser menor ou igual à data final.', {
          variant: 'warning',
          solid: false,
          noCloseButton: true,
          toaster: 'b-toaster-top-center',
        })
        return false
      }

      return true
    },
    escapeCsv(value) {
      if (value === null || value === undefined) return ''
      const str = String(value)
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`
      }
      return str
    },
    arrayToCsv(rows) {
      if (!rows.length) return ''

      const headers = Array.from(
        rows.reduce((set, row) => {
          Object.keys(row || {}).forEach((key) => set.add(key))
          return set
        }, new Set())
      )

      const lines = rows.map((row) =>
        headers.map((header) => this.escapeCsv(row ? row[header] : '')).join(',')
      )

      return [headers.join(','), ...lines].join('\n')
    },
    resolveCsvContent(payload) {
      if (!payload) return ''

      if (typeof payload === 'string') return payload
      if (Array.isArray(payload)) return this.arrayToCsv(payload)
      if (Array.isArray(payload.dados)) return this.arrayToCsv(payload.dados)
      if (Array.isArray(payload.data)) return this.arrayToCsv(payload.data)
      return this.arrayToCsv([payload])
    },
    downloadCsv(content, fileName) {
      const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', fileName)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    },
    addIntegrationLog(nome, status) {
      this.integrationLog = [
        {
          id: `${Date.now()}-${Math.random()}`,
          nome,
          status,
          horario: this.formatDateTime(new Date()),
        },
        ...this.integrationLog,
      ].slice(0, 5)
    },
    addGenerationHistory(orgao, tipoArquivo, fileName) {
      this.generationHistory = [
        {
          id: `${Date.now()}-${Math.random()}`,
          orgao,
          tipoArquivo,
          fileName,
          dataInicial: this.integrationForm.data_inicial,
          dataFinal: this.integrationForm.data_final,
          geradoEm: this.formatDateTime(new Date()),
        },
        ...this.generationHistory,
      ].slice(0, 20)
    },
    isDateInRange(dateIso, startDate, endDate) {
      const d = new Date(`${dateIso}T00:00:00`).getTime()
      const s = new Date(`${startDate}T00:00:00`).getTime()
      const e = new Date(`${endDate}T23:59:59`).getTime()
      return d >= s && d <= e
    },
    darBaixaPorHistorico(entry) {
      const orgao = entry.orgao === 'SIAGRO' ? 'SIAGRO' : 'SIGA'
      const channelField = orgao === 'SIAGRO' ? 'siagro_status' : 'siga_status'
      let updated = 0

      this.items = this.items.map((item) => {
        const isCorrectUf = orgao === 'SIAGRO' ? item.estado === 'PR' : item.estado === 'RS'
        if (!isCorrectUf) return item
        if (!this.isDateInRange(item.emissao, entry.dataInicial, entry.dataFinal)) return item
        if (item[channelField] === 'NAO_APLICAVEL' || item[channelField] === 'ENVIADO') return item

        updated += 1
        return {
          ...item,
          [channelField]: 'ENVIADO',
          observacao: `Baixa por histórico (${orgao}) em ${this.formatDateTime(new Date())}`,
        }
      })

      this.baixaResult = {
        orgao,
        periodo: `${entry.dataInicial} até ${entry.dataFinal}`,
        updated,
      }
      this.addIntegrationLog(`Baixa por histórico ${orgao}`, updated ? 'SUCESSO' : 'ALERTA')

      this.$bvToast.toast(
        updated
          ? `Baixa concluída para ${orgao}. Receitas atualizadas: ${updated}.`
          : `Nenhuma receita pendente encontrada para baixa em ${orgao} nesse período.`,
        {
          variant: updated ? 'success' : 'warning',
          solid: false,
          noCloseButton: true,
          toaster: 'b-toaster-top-center',
        }
      )
    },
    async gerarSiagro() {
      if (this.isEmpresa169()) {
        this.showDemoGenerationNotEnabledMessage()
        return
      }

      if (!this.isValidPeriod()) return

      this.isLoading = true
      const token = this.$store.state.auth.currentUser.token

      try {
        const response = await reportsService.reportSiagro(
          token,
          this.integrationForm.data_inicial,
          this.integrationForm.data_final
        )

        const csvContent = this.resolveCsvContent(response.data)
        if (!csvContent) {
          this.$bvToast.toast('A API não retornou conteúdo para gerar o arquivo SIAGRO.', {
            variant: 'warning',
            solid: false,
            noCloseButton: true,
            toaster: 'b-toaster-top-center',
          })
          this.addIntegrationLog('Geração SIAGRO', 'ALERTA')
          return
        }

        this.downloadCsv(csvContent, 'receita_siagro.csv')
        this.addGenerationHistory('SIAGRO', 'CSV', 'receita_siagro.csv')
        this.addIntegrationLog('Geração SIAGRO', 'SUCESSO')
      } catch (err) {
        const message =
          (err.response && err.response.data && (err.response.data.msg || err.response.data.message)) ||
          'Erro ao gerar arquivo SIAGRO.'

        this.$bvToast.toast(message, {
          variant: 'danger',
          solid: false,
          noCloseButton: true,
          toaster: 'b-toaster-top-center',
        })
        this.addIntegrationLog('Geração SIAGRO', 'ALERTA')
      } finally {
        this.isLoading = false
      }
    },
    async gerarSiga() {
      if (this.isEmpresa169()) {
        this.showDemoGenerationNotEnabledMessage()
        return
      }

      if (!this.isValidPeriod()) return

      if (!String(this.integrationForm.cpf || '').trim()) {
        this.$bvToast.toast('Informe o CPF para gerar o arquivo do SIG@.', {
          variant: 'warning',
          solid: false,
          noCloseButton: true,
          toaster: 'b-toaster-top-center',
        })
        return
      }

      this.isLoading = true
      const token = this.$store.state.auth.currentUser.token

      try {
        const payload = {
          funcionalidade: 'mre',
          cpf: this.integrationForm.cpf,
          data_inicial: this.integrationForm.data_inicial,
          data_final: this.integrationForm.data_final,
        }

        const response = await arquivoService.generateAndDownload(token, payload)
        const csvContent = typeof response.data === 'string' ? response.data : this.resolveCsvContent(response.data)
        const fileName = (response.headers && response.headers.file_name) || 'receita_siga.csv'

        this.downloadCsv(csvContent, fileName)
        this.addGenerationHistory('SIGA', 'CSV', fileName)
        this.addIntegrationLog('Geração SIG@', 'SUCESSO')
      } catch (err) {
        const message =
          (err.response && err.response.data && (err.response.data.msg || err.response.data.message)) ||
          'Erro ao gerar arquivo do SIG@.'

        this.$bvToast.toast(message, {
          variant: 'danger',
          solid: false,
          noCloseButton: true,
          toaster: 'b-toaster-top-center',
        })
        this.addIntegrationLog('Geração SIG@', 'ALERTA')
      } finally {
        this.isLoading = false
      }
    },
    async sincronizarSigenWebservice() {
      if (this.isEmpresa169()) {
        this.showDemoGenerationNotEnabledMessage()
        return
      }

      if (!this.isValidPeriod()) return

      this.isLoading = true
      const token = this.$store.state.auth.currentUser.token

      try {
        if (typeof reportsService.syncSigen !== 'function') {
          throw new Error('SIGEN_PENDING_BACKEND')
        }

        await reportsService.syncSigen(token, this.integrationForm.data_inicial, this.integrationForm.data_final)
        this.addIntegrationLog('Sincronização SIGEN+', 'SUCESSO')

        // Mock visual: atualiza pendentes de SC para enviado para refletir a sincronização na tela.
        this.items = this.items.map((item) => {
          if (item.estado === 'SC' && item.sigen_status === 'PENDENTE') {
            return {
              ...item,
              sigen_status: 'ENVIADO',
              observacao: 'Sincronizado via webservice SIGEN+',
            }
          }
          return item
        })

        this.$bvToast.toast('Sincronização SIGEN+ concluída com sucesso.', {
          variant: 'success',
          solid: false,
          noCloseButton: true,
          toaster: 'b-toaster-top-center',
        })
      } catch (err) {
        const pendingBackend = err && err.message === 'SIGEN_PENDING_BACKEND'
        const message = pendingBackend
          ? 'Integração SIGEN+ ainda depende do endpoint no backend. Tela pronta para conectar quando disponível.'
          : (err.response && err.response.data && (err.response.data.msg || err.response.data.message)) ||
          'Erro ao sincronizar SIGEN+.'

        this.$bvToast.toast(message, {
          variant: pendingBackend ? 'warning' : 'danger',
          solid: false,
          noCloseButton: true,
          toaster: 'b-toaster-top-center',
        })
        this.addIntegrationLog('Sincronização SIGEN+', 'ALERTA')
      } finally {
        this.isLoading = false
      }
    },
  },
}
</script>

<style scoped>
.realtime-pill {
  font-size: 11px;
  font-weight: 700;
  border-radius: 999px;
  padding: 4px 10px;
  border: 1px solid transparent;
}

.realtime-pill.is-online {
  color: #166534;
  background: #dcfce7;
  border-color: #86efac;
}

.realtime-pill.is-offline {
  color: #92400e;
  background: #fef3c7;
  border-color: #fcd34d;
}

.realtime-notice {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border: 1px solid #93c5fd;
  background: #eff6ff;
}

.realtime-notice-time {
  margin-left: 12px;
  font-size: 12px;
  color: #475569;
}

.webhook-guide {
  font-size: 13px;
  color: #334155;
}

.guide-label {
  font-size: 12px;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 6px;
}

.guide-note {
  font-size: 12px;
  color: #475569;
}

.guide-card {
  border: 1px solid #dbe4ef;
  background: #f8fafc;
  border-radius: 10px;
  padding: 12px;
}

.guide-card-title {
  font-size: 13px;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 8px;
}

.guide-mini-title {
  font-size: 12px;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 6px;
}

.guide-list {
  margin: 0;
  padding-left: 18px;
  font-size: 12px;
  color: #334155;
}

.guide-list li {
  margin-bottom: 4px;
  line-height: 1.35;
}

.guide-box {
  background: #0f172a;
  color: #e2e8f0;
  border-radius: 8px;
  padding: 10px;
  font-size: 12px;
  overflow-x: auto;
  margin-bottom: 0;
}

.summary-chip {
  display: inline-block;
  min-width: 44px;
  text-align: center;
  border-radius: 999px;
  padding: 3px 10px;
  font-size: 12px;
  font-weight: 700;
}

.summary-pending {
  color: #92400e;
  background: #fef3c7;
}

.summary-generated {
  color: #166534;
  background: #dcfce7;
}

.integration-hub {
  border: 1px solid #cfe0f4;
  border-radius: 12px;
  padding: 16px;
  background: linear-gradient(180deg, #f8fbff 0%, #ffffff 100%);
}

.integration-title {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
}

.integration-subtitle {
  font-size: 13px;
  color: #475569;
  margin-top: 4px;
  margin-bottom: 8px;
}

.sync-note {
  font-size: 12px;
  color: #475569;
}

.upload-baixa-box {
  border: 1px solid #dbe4ef;
  border-radius: 10px;
  padding: 12px;
  background: #f8fafc;
}

.upload-baixa-title {
  font-size: 13px;
  font-weight: 700;
  color: #0f172a;
}

.upload-baixa-subtitle {
  font-size: 12px;
  color: #64748b;
  margin-top: 2px;
  margin-bottom: 8px;
}

.upload-baixa-result {
  font-size: 12px;
  color: #334155;
  background: #ffffff;
  border: 1px dashed #cbd5e1;
  border-radius: 8px;
  padding: 8px;
}

.receita-sheet-wrap {
  background: #eef2f7;
  border: 1px solid #dbe4ef;
  border-radius: 10px;
  padding: 16px;
  overflow: auto;
}

.receita-doc {
  border: 1px solid #cbd5e1;
  background: #ffffff;
  width: 210mm;
  max-width: 100%;
  min-height: 297mm;
  margin: 0 auto;
  padding: 20mm 16mm 18mm;
  color: #000000;
  font-family: 'Times New Roman', Times, serif;
  font-size: 12pt;
  line-height: 1.5;
  letter-spacing: 0.1px;
  box-shadow: 0 8px 28px rgba(15, 23, 42, 0.15);
  display: flex;
  flex-direction: column;
}

.receita-doc-header {
  display: grid;
  grid-template-columns: 1.1fr 1.8fr 1fr;
  align-items: start;
  gap: 8px;
  margin-bottom: 14px;
}

.doc-brand {
  display: flex;
  align-items: center;
}

.doc-brand-logo {
  width: 150px;
  max-width: 100%;
  height: auto;
  display: block;
}

.doc-title {
  text-align: center;
  font-size: 14pt;
  font-weight: 700;
  line-height: 1.08;
  letter-spacing: 0;
  text-transform: uppercase;
}

.doc-header-right {
  text-align: right;
  font-size: 11pt;
  font-weight: 600;
  line-height: 1.35;
}

.doc-section-title {
  margin-top: 12px;
  margin-bottom: 5px;
  font-size: 12pt;
  font-weight: 700;
  letter-spacing: 0.2px;
  border-top: 1px solid #dbe4ef;
  padding-top: 5px;
}

.doc-line {
  margin-bottom: 5px;
}

.doc-row {
  display: grid;
  gap: 10px;
}

.doc-row-2 {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.doc-row-3 {
  grid-template-columns: 1.4fr 1fr 1fr;
}

.doc-row-dose {
  grid-template-columns: 1.6fr 1.1fr 0.9fr;
}

.doc-line-nowrap {
  white-space: nowrap;
}

.doc-paragraph {
  margin-bottom: 7px;
  text-align: justify;
}

.doc-warning {
  text-align: center;
  font-weight: 700;
  margin-top: 16px;
  letter-spacing: 0.2px;
}

.doc-final-text {
  margin-top: 8px;
}

.doc-city-date {
  text-align: center;
  font-size: 12.5px;
  margin-top: 6px;
}

.doc-footer {
  margin-top: auto;
  padding-top: 18px;
}

.doc-signature {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 36px;
  margin-top: 38px;
}

.doc-sign-line {
  border-top: 1px solid #000000;
  margin-bottom: 4px;
}

.doc-sign-name {
  text-align: center;
  font-weight: 700;
  font-size: 13px;
}

.doc-sign-sub {
  text-align: center;
  font-size: 12.5px;
}

:deep(.receita-modal .modal-dialog) {
  max-width: 1220px;
}

:deep(.receita-modal .modal-body) {
  padding: 12px 16px 14px;
}

@media (max-width: 991px) {
  .receita-doc {
    width: 100%;
    min-height: auto;
    padding: 16px 14px;
  }

  .receita-doc-header {
    grid-template-columns: 1fr;
    gap: 6px;
  }

  .doc-title,
  .doc-header-right {
    text-align: left;
  }

  .doc-row-2,
  .doc-row-3,
  .doc-signature {
    grid-template-columns: 1fr;
    gap: 6px;
  }
}

.history-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 220px;
  overflow-y: auto;
}

.history-item {
  border: 1px solid #dbe4ef;
  border-radius: 8px;
  background: #ffffff;
  padding: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
}

.history-main {
  min-width: 0;
}

.history-type {
  font-size: 12px;
  font-weight: 700;
  color: #0f172a;
}

.history-periodo {
  font-size: 11px;
  color: #334155;
}

.history-meta {
  font-size: 11px;
  color: #64748b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.integration-log {
  border-top: 1px dashed #cbd5e1;
  padding-top: 10px;
}

.log-title {
  font-size: 12px;
  color: #334155;
  margin-bottom: 6px;
  font-weight: 600;
}

.log-item {
  display: flex;
  gap: 10px;
  align-items: center;
  font-size: 12px;
  color: #475569;
  margin-bottom: 4px;
}

.log-name {
  min-width: 160px;
}

.log-status {
  font-weight: 700;
}

.log-status.ok {
  color: #15803d;
}

.log-status.warn {
  color: #b45309;
}

.log-time {
  color: #64748b;
}

.purchase-box {
  border: 1px solid #dbe4ef;
  border-radius: 12px;
  padding: 14px;
  background: #ffffff;
}

.purchase-title {
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
}

.purchase-subtitle {
  font-size: 12px;
  color: #64748b;
  margin-top: 4px;
}

.purchase-kpi {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 10px;
  background: #f8fafc;
}

.purchase-kpi-label {
  font-size: 11px;
  color: #64748b;
}

.purchase-kpi-value {
  font-size: 22px;
  font-weight: 700;
  color: #0f172a;
}

.purchase-flow-compact .pipeline-title {
  font-size: 12px;
  margin-bottom: 6px;
}

.purchase-flow-compact .pipeline-track {
  height: 8px;
}

.purchase-flow-compact .pipeline-legends {
  margin-top: 6px;
  gap: 8px;
}

.purchase-flow-compact .pipeline-legends span {
  font-size: 11px;
}

.purchase-badge {
  display: inline-block;
  min-width: 78px;
  text-align: center;
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 11px;
  font-weight: 700;
}

.purchase-high {
  color: #991b1b;
  background: #fee2e2;
}

.purchase-normal {
  color: #1e293b;
  background: #e2e8f0;
}

.purchase-generated {
  color: #166534;
  background: #dcfce7;
}

.purchase-pending {
  color: #92400e;
  background: #fef3c7;
}

.filter-row .form-group {
  margin-bottom: 0;
}

:deep(.filter-sidebar-body) {
  padding: 14px 14px 10px;
  background: #f8fafc;
}

.filter-sidebar {
  min-height: 100%;
  display: flex;
  flex-direction: column;
}

.filter-sidebar-header {
  margin-bottom: 12px;
  text-align: left;
  padding-left: 14px;
  padding-right: 14px;
}

.filter-sidebar-title {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
}

.filter-sidebar-subtitle {
  font-size: 12px;
  color: #64748b;
  margin-top: 4px;
  line-height: 1.4;
  padding-right: 8px;
}

.filter-panel {
  border: 1px solid #dbe4ef;
  border-radius: 12px;
  padding: 14px;
  background: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
}

.filter-group {
  margin-bottom: 12px;
}

:deep(.filter-group > label) {
  font-size: 12px;
  font-weight: 600;
  color: #334155;
  margin-bottom: 6px;
}

:deep(.filter-group .custom-select),
:deep(.filter-group .form-control) {
  min-height: 38px;
}

.filter-check {
  margin-top: 2px;
  line-height: 1.3;
}

.filter-sidebar-actions {
  margin-top: 12px;
  padding-top: 10px;
  border-top: 1px dashed #cbd5e1;
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

.action-btn {
  min-width: 120px;
  height: 38px;
}

@media (max-width: 575px) {
  .filter-sidebar-actions {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
  }

  .action-btn {
    width: 100%;
  }
}

.pipeline {
  border: 1px solid #dbe4ef;
  border-radius: 10px;
  padding: 12px;
  background: #f8fafc;
}

.pipeline-title {
  font-size: 13px;
  color: #334155;
  margin-bottom: 8px;
  font-weight: 600;
}

.pipeline-track {
  display: flex;
  width: 100%;
  height: 10px;
  border-radius: 999px;
  overflow: hidden;
  background: #e2e8f0;
}

.pipeline-segment {
  height: 100%;
}

.segment-success {
  background: #10b981;
}

.segment-warning {
  background: #f59e0b;
}

.segment-danger {
  background: #ef4444;
}

.pipeline-legends {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 8px;
  font-size: 12px;
  color: #475569;
}

.dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 6px;
}

.dot-success {
  background: #10b981;
}

.dot-warning {
  background: #f59e0b;
}

.dot-danger {
  background: #ef4444;
}

.orgao-cell {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.badge-status {
  display: inline-block;
  min-width: 90px;
  text-align: center;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
}

.badge-success {
  background: #dcfce7;
  color: #166534;
}

.badge-warning {
  background: #fef3c7;
  color: #92400e;
}

.badge-danger {
  background: #fee2e2;
  color: #991b1b;
}

.badge-neutral {
  background: #e2e8f0;
  color: #1e293b;
}
</style>
